const reliableTimers = {};
const timerExists = timer => timer in reliableTimers;
const getReliableTimerId = timer => 'RT_' + timer;

/**
 * Use this utility to invoke a function after a specified number of milliseconds.
 * When the document's visibility is changed from hidden to visible and
 * the time elapsed is >= the specified delay and the wrapped function hasn't yet run, invoke the function.
 */
export const setReliableTimeout = (wrappedFunc: () => void, delay: number): string => {
    const startTime = Date.now();
    let timer;

    const newWrappedFunc = () => {
        clearReliableTimeout(getReliableTimerId(timer));
        wrappedFunc && wrappedFunc();
    };

    const vChangeHandler = () => {
        if (!document.hidden && timerExists(timer) && Date.now() - startTime >= delay) {
            newWrappedFunc();
        }
    };

    document.addEventListener('visibilitychange', vChangeHandler);
    timer = setTimeout(newWrappedFunc, delay);
    reliableTimers[timer] = vChangeHandler;

    // decorate the timer id being returned to avoid accidentally clearing the reliable timeout using clearTimeout
    return getReliableTimerId(timer);
};

/**
 * Use this utility to clear a timer set with setReliableTimeout
 */
export const clearReliableTimeout = reliableTimerId => {
    if (typeof reliableTimerId === 'string') {
        const timer = reliableTimerId.split('_')[1];
        if (timerExists(timer)) {
            clearTimeout(+timer);
            document.removeEventListener('visibilitychange', reliableTimers[timer]);
            delete reliableTimers[timer];
        }
    }
};
